import type { PurchaseOrder } from '@ztechapps/common.contract'
import type { ConfirmDialogOptions } from '@ztechapps/common.hook'
import type { TemplateVariables } from '@ztechapps/chat.webui'
import { PurchaseOrderStatusEnum } from '@ztechapps/common.contract'
import { buildDocumentLinkPdfUrl } from '@ztechapps/common.utils'
import { useCreatePurchaseOrderDocumentLink } from '@ztechapps/purchase-order.common'
import { useTemplateResetPassword } from '@ztechapps/chat.webui'
import { appSettings } from '../../../config/app-settings'
import { useUpdatePurchaseOrderStatus } from '../../../view/purchases/item/hooks/useUpdatePurchaseOrderStatus'

export type UsePurchaseOrderTemplateVariablesResult = {
  templateVariables: TemplateVariables
  templateVariableConfirmDialogConfigs: Record<any, Partial<ConfirmDialogOptions>>
}

export const usePurchaseOrderTemplateVariables = ({
  purchaseOrder,
  hasRoleAndNotReceived,
}: {
  purchaseOrder?: PurchaseOrder
  hasRoleAndNotReceived?: boolean
} = {}): UsePurchaseOrderTemplateVariablesResult => {
  const { resetPassword: resetPasswordMain } = useTemplateResetPassword()
  const { mint } = useCreatePurchaseOrderDocumentLink()
  const { onUpdate } = useUpdatePurchaseOrderStatus({ purchaseOrderId: purchaseOrder?._id ?? '' })

  const templateVariables = {
    purchaseOrderPdfUrl: async () => {
      const { token } = await mint(purchaseOrder?._id)
      return buildDocumentLinkPdfUrl({ rootUrl: appSettings.AppUrl.store, token })
    },
    purchaseOrder,
    resetPassword: () => {
      return resetPasswordMain({ _id: purchaseOrder?.vendor?._id })
    },
    username: purchaseOrder?.vendor?.profile?.email,
    profileName: purchaseOrder?.vendor?.profile?.name,
    purchaseOrderNumber: purchaseOrder?.code,
    requestForQuotationStatus: async () => {
      if (hasRoleAndNotReceived) {
        await onUpdate(PurchaseOrderStatusEnum.RequestForQuotation)
      }
    },
    purchaseOrderStatus: async () => {
      if (hasRoleAndNotReceived) await onUpdate(PurchaseOrderStatusEnum.PurchaseOrder)
    },
  } as TemplateVariables & { requestForQuotationStatus: () => Promise<void>; purchaseOrderStatus: () => Promise<void> }

  const templateVariableConfirmDialogConfigs = {
    requestForQuotationStatus: {
      text: `Do you want to send request for quotation? ${
        hasRoleAndNotReceived
          ? `This template will instantly update the status to ${PurchaseOrderStatusEnum.RequestForQuotation}.`
          : ''
      }`,
      title: `Request for Quotation`,
      buttons: { confirm: { text: `Yes, Apply template!` } },
    },
    purchaseOrderStatus: {
      text: `Do you want to send purchase order? ${
        hasRoleAndNotReceived
          ? `This template will instantly update the status to ${PurchaseOrderStatusEnum.PurchaseOrder}.`
          : ''
      }  `,
      title: `Purchase Order`,
      buttons: { confirm: { text: `Yes, Apply template!` } },
    },
  }

  return { templateVariables, templateVariableConfirmDialogConfigs }
}
